import React from 'react';
import { CalendarIcon, ChevronDownIcon, InformationCircleIcon, UserIcon } from '@heroicons/react/16/solid';
import { CoachSessionType, SESSION_TYPE_OPTIONS, todayIsoDate } from '../sessionDetails';

const DATE_TOOLTIP =
  "The day the coaching session took place. Defaults to today; pick an earlier date if you're logging a past session. Future dates can't be selected.";
const SESSION_TYPE_TOOLTIP =
  "First session is a member's initial consultation; Follow-up session is any session after that. Pre-selected from whether the member already has submitted notes, and you can change it.";

interface SessionDetailsProps {
  sessionDate: string;
  sessionType: CoachSessionType;
  onSessionDateChange: (value: string) => void;
  onSessionTypeChange: (value: CoachSessionType) => void;
}

const InfoTooltip: React.FC<{ id: string; text: string }> = ({ id, text }) => (
  <span className="group relative inline-flex">
    <InformationCircleIcon
      className="w-4 h-4 text-gray-500"
      tabIndex={0}
      aria-describedby={id}
      aria-label="More info"
    />
    <span
      id={id}
      role="tooltip"
      className="invisible group-hover:visible group-focus-within:visible absolute left-1/2 top-full z-30 mt-2 w-64 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg"
    >
      {text}
    </span>
  </span>
);

const controlClassName =
  'w-full appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm text-primary-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const SessionDetails: React.FC<SessionDetailsProps> = ({
  sessionDate,
  sessionType,
  onSessionDateChange,
  onSessionTypeChange
}) => {
  return (
    <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mb-6">
      {/* Date of Session */}
      <div className="flex-1 min-w-0">
        <label htmlFor="session-date" className="flex items-center gap-1 graySmallText mb-2">
          Date of Session
          <InfoTooltip id="session-date-tooltip" text={DATE_TOOLTIP} />
        </label>
        <div className="relative">
          <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            id="session-date"
            type="date"
            required
            max={todayIsoDate()}
            value={sessionDate}
            onChange={(e) => onSessionDateChange(e.target.value)}
            className={controlClassName}
          />
        </div>
      </div>

      <div className="hidden sm:block w-px bg-gray-200" aria-hidden="true" />

      {/* Coach Session Type */}
      <div className="flex-1 min-w-0">
        <label htmlFor="session-type" className="flex items-center gap-1 graySmallText mb-2">
          Coach Session Type
          <InfoTooltip id="session-type-tooltip" text={SESSION_TYPE_TOOLTIP} />
        </label>
        <div className="relative">
          <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <select
            id="session-type"
            value={sessionType}
            onChange={(e) => onSessionTypeChange(e.target.value as CoachSessionType)}
            className={controlClassName}
          >
            {SESSION_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        </div>
      </div>
    </div>
  );
};

export default SessionDetails;
